import { datadogLogs, type LogsEvent, type Site } from "@datadog/browser-logs";
import { redactUrlQueryParams } from "./redactQueryParams";
import { isReportableError } from "./reportableErrors";

// Rendered into the page by the Rails `telemetry_config` helper.
export type DatadogConfig = {
  clientToken: string
  site: Site
  service: string
  env: string
  version: string
};

// Failed requests to these (status 0) are noisy: ad blockers and CORS changes outside our control.
const IGNORABLE_HTTP_URLS = [
  "smartlook",
  "doubleclick",
  "google",
  "googleadservices",
  "googlesyndication",
];

const IGNORABLE_MESSAGE_PATTERNS = [
  // Instagram in-app browser WKWebView autofill bridge error - not our code, doesn't affect UX.
  // https://developers.facebook.com/community/threads/320013549791141
  /Can't find variable: _AutofillCallbackHandler/,
];

const topStackFrame = (stack?: string) => {
  const match = stack?.match(/([a-z-]+:\/\/[^\s()]+?):(\d+):\d+/);
  return match ? { url: match[1], line: Number(match[2]) } : {};
};

const isReportableRuntimeError = (log: LogsEvent) => {
  const { message } = log;
  const stack = log.error?.stack;
  const { url, line } = topStackFrame(stack);

  // Cross-origin scripts we can't see into.
  if (!url && (/^Script error\.?$/).test(message)) { return false; }
  // Without a stack frame (e.g. a rejected non-Error) treat the error as coming from this page.
  return isReportableError(message, url ?? window.location.href, line, stack);
};

const isIgnorableHttpError = (log: LogsEvent) =>
  !!log.http && log.http.status_code === 0
    && IGNORABLE_HTTP_URLS.some(domain => log.http!.url.includes(domain));

const shouldDrop = (log: LogsEvent) =>
  isIgnorableHttpError(log)
    || (!!log.message && IGNORABLE_MESSAGE_PATTERNS.some(regex => regex.test(log.message)))
    || (log.origin === "source" && !isReportableRuntimeError(log));

export const beforeSend = (log: LogsEvent) => {
  if (shouldDrop(log)) { return false; }

  if (log.view?.url) { log.view.url = redactUrlQueryParams(log.view.url); }
  if (log.view?.referrer) { log.view.referrer = redactUrlQueryParams(log.view.referrer); }

  return true;
};

export const initDatadog = (config: DatadogConfig) => {
  datadogLogs.init({
    clientToken: config.clientToken,
    site: config.site,
    service: config.service,
    env: config.env,
    version: config.version,
    // Uncaught errors, unhandled rejections, console.error and failed network requests.
    forwardErrorsToLogs: true,
    forwardConsoleLogs: ["error"],
    sessionSampleRate: 100,
    useSecureSessionCookie: true,
    beforeSend,
  });
};

// For errors we've handled ourselves. Uncaught errors are collected by Datadog directly.
export const reportError = (message: string, error?: unknown) => {
  datadogLogs.logger.error(message, undefined, error instanceof Error ? error : undefined);
};
